import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import {
  SellerProductsApi,
  SellerProductDetailsDto,
  SellerProductListItemDto,
} from '../../services/seller-products-api';

export interface ProductRow {
  id: string;
  name: string;
  thumbnailUrl?: string;
  categoryId: string;
  brandId?: string;
  productType: string;
  status: string;
  createdAtUtc: string;
  activeDraft?: SellerProductListItemDto['activeDraft'];
}

@Injectable()
export class ProductsManager {
  private readonly api = inject(SellerProductsApi);
  private readonly cache = new Map<string, ProductRow>();

  readonly selectedProduct = signal<ProductRow | null>(null);
  readonly productDetails = signal<SellerProductDetailsDto | null>(null);
  readonly loadingDetails = signal(false);
  readonly categoryId = signal<string | null>(null);
  searchTerm = '';

  readonly store = new CustomStore({
    key: 'id',
    load: async (loadOptions: any) => {
      const skip = loadOptions.skip ?? 0;
      const take = loadOptions.take ?? 20;
      const page = Math.floor(skip / take) + 1;
      const searchValue = loadOptions.searchValue || undefined;

      const result = await firstValueFrom(
        this.api.listProducts({
          page,
          pageSize: take,
          search: searchValue,
          categoryId: this.categoryId() ?? undefined,
        }),
      );

      const rows = result.items.map((x) => this.mapItem(x));
      this.cache.clear();
      rows.forEach((row) => this.cache.set(row.id, row));
      return { data: rows, totalCount: result.totalCount };
    },
    byKey: async (key: string) => {
      const cached = this.cache.get(key);
      if (cached) return cached;
      throw new Error('Product not found');
    },
  });

  readonly dataSource = new DataSource(this.store);

  setSelectedProduct(product: ProductRow | null) {
    this.selectedProduct.set(product);
  }

  setSelectedProductById(productId: string) {
    this.selectedProduct.set(this.cache.get(productId) ?? null);
  }

  async loadProductDetails(productId: string) {
    this.loadingDetails.set(true);
    try {
      const details = await firstValueFrom(this.api.getProductDetails(productId));
      this.productDetails.set(details);
    } finally {
      this.loadingDetails.set(false);
    }
  }

  setCategoryId(categoryId: string | null) {
    if (this.categoryId() === categoryId) return;
    this.categoryId.set(categoryId);
    this.dataSource.pageIndex(0);
    this.dataSource.reload();
  }

  async publishSelectedProduct() {
    const id = this.productDetails()?.id ?? this.selectedProduct()?.id;
    const sellerId = globalThis.localStorage.getItem('seller_id');
    if (!id || !sellerId) return;
    await firstValueFrom(this.api.publishProduct(id, sellerId));
    await this.refreshViews();
  }

  async archiveSelectedProduct() {
    const id = this.productDetails()?.id ?? this.selectedProduct()?.id;
    const sellerId = globalThis.localStorage.getItem('seller_id');
    if (!id || !sellerId) return;
    await firstValueFrom(this.api.archiveProduct(id, sellerId));
    await this.refreshViews();
  }

  async deleteSelectedProduct() {
    const id = this.productDetails()?.id ?? this.selectedProduct()?.id;
    const sellerId = globalThis.localStorage.getItem('seller_id');
    if (!id || !sellerId) return;
    await firstValueFrom(this.api.deleteProduct(id, sellerId));
    await this.refreshViews();
  }

  private async refreshViews() {
    // In a real app we might want to re-fetch details or just update status locally
    const id = this.productDetails()?.id ?? this.selectedProduct()?.id;
    if (id) await this.loadProductDetails(id);
    await this.dataSource.reload();
  }

  private mapItem(item: SellerProductListItemDto): ProductRow {
    return {
      id: item.id,
      name: item.name,
      thumbnailUrl: item.thumbnailUrl,
      categoryId: item.categoryId,
      brandId: item.brandId,
      productType: item.productType,
      status: item.status,
      createdAtUtc: item.createdAtUtc,
      activeDraft: item.activeDraft,
    };
  }
  // في الـ ProductsManager أو في هذا الـ Component
  // افترض أن لديك دالة تبحث عن الاسم في الـ tree
  getCategoryName(categoryId: string, tree: any[]): string {
    for (const node of tree) {
      if (node.category.id === categoryId) {
        return node.category.translations[0]?.name; // أو حسب هيكلة الـ translation
      }
      if (node.children && node.children.length > 0) {
        const found = this.getCategoryName(categoryId, node.children);
        if (found) return found;
      }
    }
    return '';
  }
}
